/**
 * Partner Dedup & Abandoned Equipment React Hooks
 * Epic 72: CRM Duplikáció & 60 Napos Gep
 *
 * TanStack Query hooks for duplicate detection, merge, and abandoned equipment protocol.
 */

import {
  dismissDuplicate,
  listAbandonedEquipment,
  listDuplicates,
  mergePartners,
  recordAbandonedProtocol,
  type AbandonedEquipmentLog,
  type AbandonedProtocolInput,
  type DuplicateMatch,
  type MergePartnersInput,
} from '@/api/partner-dedup';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ============================================
// QUERIES
// ============================================

export function useDuplicateMatches() {
  const query = useQuery({
    queryKey: ['partner-dedup', 'duplicates'],
    queryFn: async () => {
      const res = await listDuplicates();
      return res.data;
    },
  });

  return {
    matches: (query.data ?? []) as DuplicateMatch[],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useAbandonedEquipment() {
  const query = useQuery({
    queryKey: ['abandoned-equipment', 'list'],
    queryFn: async () => {
      const res = await listAbandonedEquipment();
      return res.data;
    },
  });

  return {
    logs: (query.data ?? []) as AbandonedEquipmentLog[],
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

// ============================================
// MUTATIONS
// ============================================

export function useMergePartners() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { matchId: string; input: MergePartnersInput }) =>
      mergePartners(data.matchId, data.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-dedup'] });
      queryClient.invalidateQueries({ queryKey: ['partners'] });
    },
  });
}

export function useDismissDuplicate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (matchId: string) => dismissDuplicate(matchId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-dedup'] });
    },
  });
}

export function useRecordAbandonedProtocol() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { logId: string; input: AbandonedProtocolInput }) =>
      recordAbandonedProtocol(data.logId, data.input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['abandoned-equipment'] });
    },
  });
}
